import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  Box,
  Input,
  InputGroup,
  InputLeftElement,
  Text,
  Flex,
  Badge,
  Icon,
  IconButton,
  Tooltip,
  Portal,
  useColorModeValue,
  useDisclosure,
  Collapse,
} from '@chakra-ui/react';
import { FiCode, FiSearch, FiUser, FiBriefcase, FiSend, FiUsers, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { useSchemaDiscovery } from '../../../hooks/useSchemaDiscovery';
import useCalScheduling from '../../../hooks/useCalScheduling';
import { useCrmSettings } from '../../../contexts/CrmSettingsContext';
import { buildDealTeamVariableEntries } from '../../../utils/templateVariables';

const SENDER_VARIABLES = [
  { key: 'senderName', label: 'Your Name' },
  { key: 'senderEmail', label: 'Your Email' },
  { key: 'senderPhone', label: 'Your Phone' },
  // users/{uid}.website — synced from Google Admin (Broker_Information.Website_Link)
  { key: 'websiteLink', label: 'Your Website Link' },
];

// Cal.diy scheduling pilot — only offered when the per-user flag is on
const BOOKING_LINK_VARIABLE = { key: 'bookingLink', label: 'Your Booking Link' };

function VariableGroup({ label, icon, variables, search, onInsert }) {
  const [isOpen, setIsOpen] = useState(false);
  const headerBg = useColorModeValue('gray.50', 'gray.750');
  const hoverBg = useColorModeValue('gray.100', 'gray.600');

  const filtered = search
    ? variables.filter(v => v.label.toLowerCase().includes(search) || v.key.toLowerCase().includes(search))
    : variables;

  if (filtered.length === 0) return null;

  return (
    <Box mb={1}>
      <Flex
        align="center"
        gap={1.5}
        px={2}
        py={1.5}
        bg={headerBg}
        cursor="pointer"
        onClick={() => setIsOpen(!isOpen)}
        _hover={{ opacity: 0.8 }}
        borderRadius="sm"
      >
        <Icon as={isOpen ? FiChevronDown : FiChevronRight} boxSize={3} color="gray.500" />
        {icon && <Icon as={icon} boxSize={3} color="gray.500" />}
        <Text fontSize="xs" fontWeight="bold" color="gray.600" _dark={{ color: 'gray.300' }}>{label}</Text>
        <Badge fontSize="9px" colorScheme="gray" ml="auto">{filtered.length}</Badge>
      </Flex>
      <Collapse in={isOpen}>
        {filtered.map(v => (
          <Flex key={v.key} align="center" px={3} py={1} cursor="pointer" _hover={{ bg: hoverBg }} onClick={() => onInsert(v.key)} gap={2}>
            <Text fontSize="xs" flex={1}>{v.label}</Text>
            <Text fontSize="10px" color="gray.400" fontFamily="mono">{`{{${v.key}}}`}</Text>
          </Flex>
        ))}
      </Collapse>
    </Box>
  );
}

/**
 * Drop-in replacement for the old VariableInsertButton.
 * Works with TipTap editors via the `editor` prop, or with any other
 * input (e.g. a textarea) via the `onInsert` prop, which receives the
 * ready-to-insert tag string ("{{contact.firstName}}").
 * Loads ALL contact and deal columns dynamically from the schema.
 */
export default function DynamicVariableInsertButton({ editor, onInsert }) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const { getContactVariableGroups, getDealVariableGroups } = useSchemaDiscovery();
  const { dealPartyRoles } = useCrmSettings();
  // Offer {{bookingLink}} only once the user actually has a default booking
  // page (it resolves to an empty string otherwise).
  const { defaultBookingUrl } = useCalScheduling();
  const senderVariables = defaultBookingUrl
    ? [...SENDER_VARIABLES, BOOKING_LINK_VARIABLE]
    : SENDER_VARIABLES;
  const dealTeamVariables = useMemo(
    () => buildDealTeamVariableEntries(dealPartyRoles),
    [dealPartyRoles]
  );

  const bg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (
        containerRef.current?.contains(e.target) ||
        panelRef.current?.contains(e.target)
      ) return;
      onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [isOpen, onClose]);

  const contactGroups = useMemo(() => getContactVariableGroups(), [getContactVariableGroups]);
  const dealGroups = useMemo(() => getDealVariableGroups(), [getDealVariableGroups]);

  const insert = (key) => {
    if (onInsert) {
      onInsert(`{{${key}}}`);
    } else if (editor) {
      editor.chain().focus().insertContent(`{{${key}}}`).run();
    } else {
      return;
    }
    onClose();
    setSearch('');
  };

  const searchLower = search.toLowerCase();

  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const handleToggle = () => {
    if (!isOpen) {
      const rect = btnRef.current?.getBoundingClientRect();
      if (rect) {
        setDropdownPos({
          top: rect.bottom + 4,
          left: Math.max(8, rect.right - 300),
        });
      }
      onOpen();
    } else {
      onClose();
    }
  };

  return (
    <Box ref={containerRef} display="inline-block">
      <Tooltip label="Insert Variable" placement="top" hasArrow openDelay={400}>
        <IconButton
          ref={btnRef}
          icon={<FiCode />}
          size="xs"
          variant="ghost"
          aria-label="Insert variable"
          onClick={handleToggle}
        />
      </Tooltip>

      {isOpen && (
        <Portal>
          <Box
            ref={panelRef}
            position="fixed"
            top={`${dropdownPos.top}px`}
            left={`${dropdownPos.left}px`}
            bg={bg}
            border="1px solid"
            borderColor={borderColor}
            borderRadius="md"
            shadow="xl"
            zIndex={2000}
            w="300px"
            maxH="350px"
            overflowY="auto"
          >
          <Box p={2} position="sticky" top={0} bg={bg} zIndex={1} borderBottomWidth="1px" borderColor={borderColor}>
            <InputGroup size="xs">
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" boxSize={3} />
              </InputLeftElement>
              <Input placeholder="Search variables..." value={search} onChange={e => setSearch(e.target.value)} autoFocus />
            </InputGroup>
          </Box>

          {contactGroups.map(g => (
            <VariableGroup key={`c-${g.label}`} label={g.label} icon={FiUser} variables={g.variables} search={searchLower} onInsert={insert} />
          ))}
          {dealGroups.map(g => (
            <VariableGroup key={`d-${g.label}`} label={g.label} icon={FiBriefcase} variables={g.variables} search={searchLower} onInsert={insert} />
          ))}
          <VariableGroup label="Deal Team" icon={FiUsers} variables={dealTeamVariables} search={searchLower} onInsert={insert} />
          <VariableGroup label="Sender (You)" icon={FiSend} variables={senderVariables} search={searchLower} onInsert={insert} />
          </Box>
        </Portal>
      )}
    </Box>
  );
}
